import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, CheckCircle, Clock, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { downloadExport } from '@/lib/exporter';

export const StatementsPage = () => {
  const { t } = useLanguage();
  const [statements, setStatements] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/partner-statements');
      setStatements(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const downloadPdf = async (statementId, statementNumber) => {
    try {
      const response = await api.get(`/partner-statements/${statementId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${statementNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download');
    }
  };

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>{t('nav.statements')}</h1>
        <Button data-testid="export-partner-statements-btn" variant="outline" size="sm" onClick={() => downloadExport('/exports/partner-statements', 'PartnerStatements.xlsx')}>
          <DownloadSimple size={14} className="mr-2" /> Export
        </Button>
      </div>

      <div className="space-y-3">
        {statements.map((s) => (
          <Card key={s.id} data-testid={`statement-${s.id}`} className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
              <div>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>{s.statement_number}</h3>
                <p className="text-sm text-slate-600">Period: {String(s.month).padStart(2, '0')}/{s.year}</p>
                <p className="text-2xl font-bold mt-2" style={{ color: '#1E3A8A' }}>RM {s.total_amount?.toFixed(2)}</p>
              </div>
              <div className="flex flex-col gap-2">
                {s.status === 'paid' ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle size={20} weight="fill" />
                    <div>
                      <p className="font-semibold">Paid</p>
                      {s.paid_at && <p className="text-xs">{new Date(s.paid_at).toLocaleDateString()}</p>}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-600">
                    <Clock size={20} weight="fill" />
                    <p className="font-semibold">Processing</p>
                  </div>
                )}
                {s.pdf_url && (
                  <Button onClick={() => downloadPdf(s.id, s.statement_number)} variant="outline" size="sm">
                    <Download size={14} className="mr-1" /> PDF
                  </Button>
                )}
                <Button data-testid={`view-stmt-${s.id}`} onClick={() => downloadPdf(s.id, s.statement_number)} variant="outline" size="sm">
                  <Download size={14} className="mr-1" /> View / Download PDF
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {statements.length === 0 && (
          <p className="text-center text-slate-500 py-8">{t('common.noData')}</p>
        )}
      </div>
    </div>
  );
};
